// src/components/admin/products/tabs/ProductDetails.tsx
// Aba 4 — Detalhes do Produto (blocos de conteúdo)
// Migrado de: src/components/admin/ProductContentManager.tsx
// Prefixo CSS mantido: pcm-

import React, { useState, useMemo } from 'react';
import { Search, Edit, Eye, Package } from 'lucide-react';
import './ProductDetails.css';
import { ProductBlockEditor } from '../../ProductBlockEditor';
import { useMarketplaceProducts, useInvalidate } from '../../../../hooks/useAdminQueries';

interface Product {
    _id: string;
    title: string;
    type: 'deck' | 'combo' | 'cycle' | 'custom_deck';
    active: boolean;
    contentBlocks: any[];
    refDetails?: {
        name: string;
        totalCards?: number;
        deckCount?: number;
    };
}

interface ProductDetailsProps {
    onShowToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

const ProductDetails: React.FC<ProductDetailsProps> = () => {
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
    const [showEditor, setShowEditor] = useState(false);

    const { data, isLoading: loading } = useMarketplaceProducts();
    const invalidate = useInvalidate();

    const filteredProducts = useMemo(() => {
        const products: Product[] = data?.products ?? [];
        if (!searchQuery.trim()) return products;
        return products.filter((p: Product) =>
            p.title.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [searchQuery, data]);

    const handleEditContent = (product: Product) => { setSelectedProduct(product); setShowEditor(true); };
    const handleCloseEditor = () => { setShowEditor(false); setSelectedProduct(null); };
    const handleSave = () => { invalidate.products(); handleCloseEditor(); };

    const getTypeLabel = (type: string) => {
        const map: Record<string, string> = { deck: 'Deck', combo: 'Combo', cycle: 'Ciclo', custom_deck: 'Custom Deck' };
        return map[type] ?? type;
    };

    const getTypeBadgeClass = (type: string) => {
        const map: Record<string, string> = {
            deck: 'pcm-badge-deck', combo: 'pcm-badge-combo',
            cycle: 'pcm-badge-cycle', custom_deck: 'pcm-badge-custom'
        };
        return map[type] ?? '';
    };

    if (loading) return (
        <div className="pcm-container">
            <div className="pcm-loading">
                <div className="pcm-spinner" />
                <p>Carregando produtos...</p>
            </div>
        </div>
    );

    return (
        <div className="pcm-container">
            <div className="pcm-header">
                <div>
                    <h1>Detalhes dos Produtos</h1>
                    <p className="pcm-subtitle">Edite blocos de conteúdo: textos, vídeos e previews de cards</p>
                </div>
            </div>

            <div className="pcm-search-section">
                <div className="pcm-search-box">
                    <Search size={20} />
                    <input
                        type="text"
                        placeholder="Buscar produto..."
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                    />
                </div>
                <div className="pcm-results-count">{filteredProducts.length} produto(s) encontrado(s)</div>
            </div>

            <div className="pcm-products-grid">
                {filteredProducts.length === 0 ? (
                    <div className="pcm-empty">
                        <Package size={48} />
                        <p>Nenhum produto encontrado</p>
                    </div>
                ) : (
                    filteredProducts.map((product: Product) => (
                        <div key={product._id} className={`pcm-product-card ${!product.active ? 'pcm-inactive' : ''}`}>
                            <div className="pcm-card-header">
                                <div className="pcm-card-badges">
                                    <span className={`pcm-badge ${getTypeBadgeClass(product.type)}`}>
                                        {getTypeLabel(product.type)}
                                    </span>
                                    {!product.active && <span className="pcm-badge pcm-badge-inactive">Inativo</span>}
                                </div>
                                <div className="pcm-blocks-indicator">
                                    {product.contentBlocks?.length > 0
                                        ? <span className="pcm-has-blocks">✓ {product.contentBlocks.length} bloco(s)</span>
                                        : <span className="pcm-no-blocks">Sem conteúdo</span>
                                    }
                                </div>
                            </div>
                            <div className="pcm-card-body">
                                <h3>{product.title}</h3>
                                {product.refDetails && (
                                    <p className="pcm-ref-details">
                                        {product.refDetails.name}
                                        {product.refDetails.totalCards !== undefined && <span> • {product.refDetails.totalCards} cards</span>}
                                        {product.refDetails.deckCount !== undefined && <span> • {product.refDetails.deckCount} decks</span>}
                                    </p>
                                )}
                            </div>
                            <div className="pcm-card-actions">
                                <button className="pcm-btn-edit" onClick={() => handleEditContent(product)}>
                                    <Edit size={16} /> Editar Conteúdo
                                </button>
                                <button className="pcm-btn-preview" onClick={() => window.open(`/marketplace/${product._id}`, '_blank')}>
                                    <Eye size={16} />
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {showEditor && selectedProduct && (
                <ProductBlockEditor
                    productId={selectedProduct._id}
                    onClose={handleCloseEditor}
                    onSave={handleSave}
                />
            )}
        </div>
    );
};

export default ProductDetails;